import { useState } from "react";
import axios from "axios";
import { Loader2, Send, CheckCircle2, Phone, Mail, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TREATMENT_KEYS = [
    "Cardiac Surgery",
    "Neurosurgery",
    "GI & Liver Surgery",
    "Orthopaedics & Joint Replacement",
    "Oncology & Cancer Care",
    "Organ Transplant",
    "Fertility & IVF",
    "Ophthalmology",
    "Bariatric Surgery",
    "Cosmetic & Plastic Surgery",
    "Other",
];

const COUNTRY_KEYS = [
    "Nigeria",
    "Kenya",
    "Ethiopia",
    "Tanzania",
    "Oman",
    "UAE",
    "Iraq",
    "Yemen",
    "Bangladesh",
    "Nepal",
    "Sri Lanka",
    "Afghanistan",
    "Uzbekistan",
    "Russia",
    "France",
    "Other",
];

export const EnquiryForm = () => {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        country: "",
        treatment: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (
            !form.name ||
            !form.email ||
            !form.phone ||
            !form.country ||
            !form.treatment
        ) {
            toast.error(t("enquiry.error_required"));
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            setDone(true);
            toast.success(t("enquiry.success_toast"));
            setForm({
                name: "",
                email: "",
                phone: "",
                country: "",
                treatment: "",
                message: "",
            });
        } catch (err) {
            console.error(err);
            toast.error(
                err?.response?.data?.detail?.[0]?.msg ||
                    t("enquiry.error_generic")
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="enquiry"
            data-testid="enquiry-section"
            className="relative py-20 md:py-28 bg-gradient-to-br from-brand-50/60 via-white to-orange-50/40"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
                {/* Left info */}
                <div className="lg:col-span-5">
                    <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-3">
                        {t("enquiry.eyebrow")}
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-stone-900 leading-[1.05]">
                        {t("enquiry.title")}
                    </h2>
                    <p className="mt-5 text-stone-600 text-base md:text-lg max-w-md">
                        {t("enquiry.subtitle")}
                    </p>

                    <div className="mt-10 space-y-5">
                        <div className="flex items-start gap-4">
                            <div className="h-11 w-11 rounded-xl bg-white border border-stone-200 flex items-center justify-center text-brand-700">
                                <Phone className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wider text-stone-500">
                                    {t("enquiry.helpline_label")}
                                </div>
                                <div className="font-display text-lg font-semibold text-stone-900" dir="ltr">
                                    +91 79829 59943
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="h-11 w-11 rounded-xl bg-white border border-stone-200 flex items-center justify-center text-brand-700">
                                <Mail className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wider text-stone-500">
                                    {t("enquiry.email_label")}
                                </div>
                                <div className="font-display text-lg font-semibold text-stone-900" dir="ltr">
                                    support@truhealindia.com
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="h-11 w-11 rounded-xl bg-white border border-stone-200 flex items-center justify-center text-brand-700">
                                <MapPin className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wider text-stone-500">
                                    {t("enquiry.office_label")}
                                </div>
                                <div className="font-display text-lg font-semibold text-stone-900">
                                    {t("enquiry.office_value")}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right form */}
                <div className="lg:col-span-7">
                    <div className="rounded-3xl bg-white border border-stone-200 shadow-xl p-6 md:p-10">
                        {done ? (
                            <div
                                data-testid="enquiry-success"
                                className="text-center py-10"
                            >
                                <div className="mx-auto h-16 w-16 rounded-full bg-brand-50 flex items-center justify-center mb-5">
                                    <CheckCircle2 className="h-8 w-8 text-brand-700" />
                                </div>
                                <h3 className="font-display text-2xl font-bold text-stone-900">
                                    {t("enquiry.success_title")}
                                </h3>
                                <p className="mt-3 text-stone-600 max-w-sm mx-auto">
                                    {t("enquiry.success_msg")}
                                </p>
                                <Button
                                    onClick={() => setDone(false)}
                                    className="mt-6 rounded-full bg-orange-500 hover:bg-orange-600"
                                    data-testid="enquiry-submit-another"
                                >
                                    {t("enquiry.submit_another")}
                                </Button>
                            </div>
                        ) : (
                            <form
                                onSubmit={submit}
                                data-testid="enquiry-form"
                                className="space-y-5"
                            >
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <Label htmlFor="name">
                                            {t("enquiry.fullname")}
                                        </Label>
                                        <Input
                                            id="name"
                                            data-testid="enquiry-name"
                                            value={form.name}
                                            onChange={(e) =>
                                                update("name", e.target.value)
                                            }
                                            placeholder={t("enquiry.fullname_ph")}
                                            className="h-12 rounded-xl"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">
                                            {t("enquiry.email")}
                                        </Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            data-testid="enquiry-email"
                                            value={form.email}
                                            onChange={(e) =>
                                                update("email", e.target.value)
                                            }
                                            placeholder={t("enquiry.email_ph")}
                                            className="h-12 rounded-xl"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <Label htmlFor="phone">
                                            {t("enquiry.phone")}
                                        </Label>
                                        <Input
                                            id="phone"
                                            data-testid="enquiry-phone"
                                            value={form.phone}
                                            onChange={(e) =>
                                                update("phone", e.target.value)
                                            }
                                            placeholder={t("enquiry.phone_ph")}
                                            className="h-12 rounded-xl"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>{t("enquiry.country")}</Label>
                                        <Select
                                            value={form.country}
                                            onValueChange={(v) =>
                                                update("country", v)
                                            }
                                        >
                                            <SelectTrigger
                                                data-testid="enquiry-country"
                                                className="h-12 rounded-xl"
                                            >
                                                <SelectValue
                                                    placeholder={t(
                                                        "enquiry.country_ph"
                                                    )}
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {COUNTRY_KEYS.map((c) => (
                                                    <SelectItem
                                                        key={c}
                                                        value={c}
                                                        data-testid={`enquiry-country-${c.toLowerCase()}`}
                                                    >
                                                        {t(`enquiry.countries.${c}`)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>{t("enquiry.treatment")}</Label>
                                    <Select
                                        value={form.treatment}
                                        onValueChange={(v) =>
                                            update("treatment", v)
                                        }
                                    >
                                        <SelectTrigger
                                            data-testid="enquiry-treatment"
                                            className="h-12 rounded-xl"
                                        >
                                            <SelectValue
                                                placeholder={t(
                                                    "enquiry.treatment_ph"
                                                )}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {TREATMENT_KEYS.map((tr) => (
                                                <SelectItem
                                                    key={tr}
                                                    value={tr}
                                                    data-testid={`enquiry-treatment-${tr.split(" ")[0].toLowerCase()}`}
                                                >
                                                    {t(`enquiry.treatments.${tr}`)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="message">
                                        {t("enquiry.message")}
                                    </Label>
                                    <Textarea
                                        id="message"
                                        data-testid="enquiry-message"
                                        value={form.message}
                                        onChange={(e) =>
                                            update("message", e.target.value)
                                        }
                                        rows={4}
                                        placeholder={t("enquiry.message_ph")}
                                        className="rounded-xl"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="enquiry-submit"
                                    className="w-full sm:w-auto rounded-full bg-orange-500 hover:bg-orange-600 text-white h-12 px-8 text-base shadow-lg hover:-translate-y-0.5 transition-all"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 me-2 animate-spin" />
                                            {t("enquiry.submitting")}
                                        </>
                                    ) : (
                                        <>
                                            <Send className="h-4 w-4 me-2" />
                                            {t("enquiry.submit")}
                                        </>
                                    )}
                                </Button>
                                <p className="text-xs text-stone-500">
                                    {t("enquiry.privacy")}
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
